import React, { useState, useEffect, useCallback } from 'react';
import { SubscriptionTier } from '../types/synclothe';
import { useToasts } from './TactileToasts';

interface SubscriptionManagerProps {
  subscriptionTier: SubscriptionTier;
  credits: number;
  expiryDate: string | null;
  nextCycleTier?: SubscriptionTier;
  gracePeriodDaysRemaining?: number;
  isSoftLocked?: boolean;
  userEmail?: string;
  userName?: string;
}

type Currency = 'INR' | 'USD' | 'EUR' | 'GBP' | 'SGD';

interface CurrencyPricing {
  symbol: string;
  rateLabel: string;
  prices: {
    Standard: number;
    Premium: number;
    Topup10: number;
  };
}

const CURRENCIES: Record<Currency, CurrencyPricing> = {
  'INR': {
    symbol: '₹',
    rateLabel: 'INR',
    prices: { Standard: 800, Premium: 2000, Topup10: 400 }
  },
  'USD': {
    symbol: '$',
    rateLabel: 'USD',
    prices: { Standard: 12, Premium: 29, Topup10: 6 }
  },
  'EUR': {
    symbol: '€',
    rateLabel: 'EUR',
    prices: { Standard: 11, Premium: 27, Topup10: 5.5 }
  },
  'GBP': {
    symbol: '£',
    rateLabel: 'GBP',
    prices: { Standard: 9.5, Premium: 23.5, Topup10: 4.75 }
  },
  'SGD': {
    symbol: 'S$',
    rateLabel: 'SGD',
    prices: { Standard: 16, Premium: 39, Topup10: 8 }
  }
};

const detectUserRegion = (): { detectedCurrency: Currency; detectedCountry: string; detectedState: string; isHaryana: boolean; regionLabel: string } => {
  try {
    const tz = (Intl.DateTimeFormat().resolvedOptions().timeZone || '').toLowerCase();
    const locale = (navigator.language || '').toLowerCase();

    // India
    if (tz.includes('calcutta') || tz.includes('kolkata') || locale.endsWith('-in') || locale.startsWith('hi')) {
      return {
        detectedCurrency: 'INR',
        detectedCountry: 'IN',
        detectedState: 'Haryana',
        isHaryana: true,
        regionLabel: 'India (Domestic)'
      };
    }

    // Singapore
    if (tz.includes('singapore') || locale.endsWith('-sg')) {
      return {
        detectedCurrency: 'SGD',
        detectedCountry: 'SG',
        detectedState: 'Singapore',
        isHaryana: false,
        regionLabel: 'Singapore'
      };
    }

    // United Kingdom
    if (tz.includes('london') || locale === 'en-gb') {
      return {
        detectedCurrency: 'GBP',
        detectedCountry: 'GB',
        detectedState: 'London',
        isHaryana: false,
        regionLabel: 'United Kingdom'
      };
    }

    // Europe
    if (tz.startsWith('europe/') || ['de', 'fr', 'es', 'it', 'nl'].some(l => locale.startsWith(l))) {
      return {
        detectedCurrency: 'EUR',
        detectedCountry: 'EU',
        detectedState: 'Europe',
        isHaryana: false,
        regionLabel: 'European Union'
      };
    }

    // Americas & Global
    return {
      detectedCurrency: 'USD',
      detectedCountry: 'US',
      detectedState: 'Global',
      isHaryana: false,
      regionLabel: 'Americas / Global'
    };
  } catch (e) {
    return {
      detectedCurrency: 'INR',
      detectedCountry: 'IN',
      detectedState: 'Haryana',
      isHaryana: true,
      regionLabel: 'India (Default)'
    };
  }
};

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({
  subscriptionTier,
  credits,
  expiryDate,
  nextCycleTier,
  gracePeriodDaysRemaining,
  isSoftLocked,
  userEmail,
  userName
}) => {
  const { showToast } = useToasts();

  const [currency, setCurrency] = useState<Currency>('INR');
  const [detectedLabel, setDetectedLabel] = useState<string>('Detecting...');
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionTier | '10_CREDITS'>('Premium');
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponFeedback, setCouponFeedback] = useState<{ isValid: boolean; message: string; discountPercent?: number } | null>(null);

  // Verified Customer Identity & Billing Details
  const [billingName, setBillingName] = useState(userName || '');
  const [billingEmail, setBillingEmail] = useState(userEmail || '');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Keep state in sync if user profile loads after initial mount
  useEffect(() => {
    if (userName) {
      setBillingName((prev) => prev || userName);
    }
  }, [userName]);

  useEffect(() => {
    if (userEmail) {
      setBillingEmail((prev) => prev || userEmail);
    }
  }, [userEmail]);

  // Billing state for domestic Indian GST calculation
  const [billingState, setBillingState] = useState('Haryana');
  const [isHaryana, setIsHaryana] = useState(true);

  // Invoices list
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Auto-Detect Region & Currency on Mount
  useEffect(() => {
    const { detectedCurrency, detectedState, isHaryana: hr, regionLabel } = detectUserRegion();
    setCurrency(detectedCurrency);
    setBillingState(detectedState);
    setIsHaryana(detectedCurrency === 'INR' ? hr : false);
    setDetectedLabel(regionLabel);
  }, []);

  const handleCurrencySelect = (cur: Currency) => {
    setCurrency(cur);
    if (cur === 'INR') {
      setBillingState('Haryana');
      setIsHaryana(true);
    } else {
      setIsHaryana(false);
    }
  };

  const curConfig = CURRENCIES[currency];

  const fetchInvoices = useCallback(async () => {
    try {
      setLoadingInvoices(true);
      const res = await fetch('/api/getInvoices');
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
      }
    } catch (e) {
      console.warn("Could not fetch invoices", e);
    } finally {
      setLoadingInvoices(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();

    // Catch payment redirect or status parameters from gateway
    try {
      const params = new URLSearchParams(window.location.search);
      const paymentStatus = params.get('payment');
      if (paymentStatus === 'success') {
        const txn = params.get('txn');
        showToast(`✨ Payment successful! Plan upgraded.${txn ? ` (Txn: ${txn})` : ''}`, 'success');
        window.history.replaceState({}, '', window.location.pathname);
      } else if (paymentStatus === 'failed') {
        const reason = params.get('reason') || 'Payment processing was not completed. Please try again.';
        const txn = params.get('txn');
        showToast(`⚠️ Payment notice: ${reason}${txn ? ` (Ref: ${txn})` : ''}`, 'error');
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch {}
  }, [fetchInvoices, showToast]);

  const isStandardActive = subscriptionTier === 'Standard' && !!expiryDate && new Date(expiryDate) > new Date();
  const isPremiumActive = subscriptionTier === 'Premium' && (!expiryDate || new Date(expiryDate) > new Date());
  const standardDaysRemaining = isStandardActive ? Math.max(1, Math.ceil((new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : 0;

  const TIER_RANKS: Record<string, number> = { 'Explorer': 1, 'Standard': 2, 'Premium': 3 };

  const getButtonLabel = (planId: string) => {
    if (planId === 'Explorer') {
      return subscriptionTier === 'Explorer' ? 'Core Logic Deployed' : 'Baseline Discovery Tier';
    }

    if (planId === 'Standard' && isPremiumActive) {
      return 'Included in Premium';
    }

    if (subscriptionTier === planId) {
      if (planId === 'Standard') {
        if (isStandardActive) return `Active Cycle (${standardDaysRemaining}d remaining)`;
        return 'Renew Standard Plan';
      }
      if (nextCycleTier) return `Cancel Downgrade & Extend`;
      return 'Extend Logic Cycle';
    }

    const currentRank = TIER_RANKS[subscriptionTier];
    const targetRank = TIER_RANKS[planId];

    if (subscriptionTier === 'Explorer') return `Initialize ${planId}`;
    if (targetRank > currentRank) return 'Immediate Escalation';
    if (targetRank < currentRank) return 'Schedule Succession';

    return `Deploy ${planId}`;
  };

  // Top-Up Availability Logic:
  // Top-Up unlocks exclusively when the user is in an active Standard cycle AND credit balance is 0.
  const canTopup = isStandardActive && (credits === 0);

  let topupDisabledReason = '';
  if (subscriptionTier === 'Premium') {
    topupDisabledReason = 'Infinite AI Generation Credits active in Premium';
  } else if (subscriptionTier === 'Explorer') {
    topupDisabledReason = 'Deploy Standard tier to enable AI generation credits';
  } else if (subscriptionTier === 'Standard') {
    if (credits > 0) {
      topupDisabledReason = `${credits} Credit${credits > 1 ? 's' : ''} Remaining • Top-Up unlocks when balance reaches 0`;
    } else if (!isStandardActive) {
      topupDisabledReason = 'Standard cycle expired • Please renew your monthly plan';
    }
  }

  const openCheckout = (plan: SubscriptionTier | '10_CREDITS') => {
    setSelectedPlan(plan);
    setCouponCode('');
    setCouponFeedback(null);
    setEmailError(null);
    if (!billingName && userName) setBillingName(userName);
    if (!billingEmail && userEmail) setBillingEmail(userEmail);
    setShowCheckoutModal(true);
  };

  // Compute live price breakdown
  const rawBasePrice = selectedPlan === '10_CREDITS' 
    ? curConfig.prices.Topup10 
    : curConfig.prices[selectedPlan as 'Standard' | 'Premium'] || 0;

  const discountPercent = couponFeedback?.isValid && couponFeedback.discountPercent ? couponFeedback.discountPercent : 0;
  const discountAmount = couponFeedback?.isValid ? Number((rawBasePrice * (discountPercent / 100)).toFixed(2)) : 0;
  const taxableValue = Math.max(0, Number((rawBasePrice - discountAmount).toFixed(2)));

  const isDomestic = currency === 'INR';
  const cgstRate = isDomestic && isHaryana ? 0.09 : 0;
  const sgstRate = isDomestic && isHaryana ? 0.09 : 0;
  const igstRate = isDomestic && !isHaryana ? 0.18 : 0;

  const cgstAmount = Number((taxableValue * cgstRate).toFixed(2));
  const sgstAmount = Number((taxableValue * sgstRate).toFixed(2));
  const igstAmount = Number((taxableValue * igstRate).toFixed(2));
  const totalTax = Number((cgstAmount + sgstAmount + igstAmount).toFixed(2));
  const finalPayable = Number((taxableValue + totalTax).toFixed(2));

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponFeedback(null);

    const norm = couponCode.trim().toUpperCase();

    // Fast client pre-validation for BETA50 & BETA10
    if (norm === 'BETA50' || norm === 'SYNCLOTHE50') {
      setCouponFeedback({ isValid: true, message: '✨ 50% Beta Tester Discount Applied!', discountPercent: 50 });
      setCouponLoading(false);
      return;
    }

    if (norm === 'BETA10' || norm.startsWith('SYN-BETA-') || norm === '8X9P-9988-FUL') {
      setCouponFeedback({ isValid: true, message: '✨ 100% Free Beta Trial Access Applied!', discountPercent: 100 });
      setCouponLoading(false);
      return;
    }

    // Call initPayment preflight validation for dynamic coupons
    try {
      const res = await fetch('/api/initPayment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: selectedPlan,
          currency,
          couponCode: norm,
          validateOnly: true,
          billingDetails: {
            customerName: billingName.trim() || userName || 'Customer',
            customerEmail: billingEmail.trim().toLowerCase()
          }
        })
      });
      const data = await res.json();
      if (res.ok && data.couponApplied) {
        setCouponFeedback({
          isValid: true,
          message: data.couponApplied.message || 'Promo code applied!',
          discountPercent: data.couponApplied.discountPercent || 0
        });
      } else {
        setCouponFeedback({ isValid: false, message: data.error || 'Invalid or expired promo code.' });
      }
    } catch (err: any) {
      setCouponFeedback({ isValid: false, message: 'Failed to verify promo code.' });
    } finally {
      setCouponLoading(false);
    }
  };

  const handleProceedPayment = async () => {
    const trimmedEmail = billingEmail.trim().toLowerCase();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!trimmedEmail || !emailRegex.test(trimmedEmail) || trimmedEmail.endsWith('@synclothe.user')) {
      setEmailError('A valid email address is required for official GST tax invoicing and bank receipt.');
      showToast('Valid billing email required for tax invoice.', 'error');
      return;
    }
    setEmailError(null);

    try {
      showToast("Initializing Razorpay Gateway...", "logic");

      const res = await fetch('/api/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: selectedPlan,
          currency,
          couponCode: couponCode.trim().toUpperCase(),
          billingDetails: {
            customerName: billingName.trim() || userName || 'Customer',
            customerEmail: trimmedEmail,
            country: isDomestic ? 'IN' : (currency === 'GBP' ? 'GB' : currency === 'EUR' ? 'EU' : currency === 'SGD' ? 'SG' : 'US'),
            state: isDomestic ? billingState : undefined,
            stateCode: isDomestic ? (isHaryana ? '06' : '00') : undefined
          }
        })
      });

      const data = await res.json();

      if (!res.ok) {
        showToast(data.error || "Payment initialization failed.", "error");
        return;
      }

      // Handle 100% Free Trial Coupon Instant Activation
      if (data.isFreeTrial) {
        showToast(data.message || "✨ Beta Trial Activated!", "success");
        setShowCheckoutModal(false);
        setTimeout(() => window.location.reload(), 1200);
        return;
      }

      // Open Razorpay Standard Checkout Modal
      const rzpKey = data.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;

      if (!rzpKey) {
        showToast("Razorpay Key ID is not configured.", "error");
        return;
      }

      if (typeof (window as any).Razorpay === 'undefined') {
        showToast("Razorpay SDK is loading. Please wait a moment and try again.", "error");
        return;
      }

      const options = {
        key: rzpKey,
        amount: data.amount,
        currency: data.currency,
        name: "Synclothe",
        description: `${selectedPlan} Tier Subscription`,
        image: "/icon.svg",
        order_id: data.order_id || data.id,
        prefill: {
          name: billingName.trim() || userName || 'Customer',
          email: trimmedEmail,
        },
        theme: {
          color: "#0a84ff"
        },
        modal: {
          ondismiss: () => {
            showToast("Payment checkout cancelled by user.", "logic");
          }
        },
        handler: async (response: any) => {
          showToast("Verifying payment with bank...", "sync");
          try {
            const verifyRes = await fetch('/api/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                tier: selectedPlan,
                currency: data.currency,
                couponCode: couponCode.trim().toUpperCase(),
                customerEmail: trimmedEmail,
                customerName: billingName.trim() || userName || 'Customer',
                state: isDomestic ? billingState : undefined,
                stateCode: isDomestic ? (isHaryana ? '06' : '00') : undefined
              })
            });

            const verifyData = await verifyRes.json();
            if (verifyRes.ok && verifyData.success) {
              showToast("✨ Payment verified successfully! Subscription activated.", "success");
              setShowCheckoutModal(false);
              fetchInvoices();
              setTimeout(() => window.location.reload(), 1500);
            } else {
              showToast(verifyData.error || "Payment verification failed.", "error");
            }
          } catch (verifyErr) {
            showToast("Payment verification error. Please contact support.", "error");
          }
        }
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.on('payment.failed', (resp: any) => {
        showToast(`Payment failed: ${resp.error?.description || "Transaction declined."}`, "error");
      });
      rzp.open();
    } catch (e: any) {
      showToast("Payment execution failed. Please try again.", "error");
    }
  };

  return (
    <div className="space-y-12">
      
      {/* HEADER & MULTI-CURRENCY SWITCHER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-dk-800/40 p-6 rounded-2xl border border-white/5">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-white italic uppercase tracking-tighter">
            Intelligence <span className="text-azure-400">Subscription Matrix</span>
          </h2>
          <p className="text-xs text-gray-400 font-mono mt-1">
            Global high-fashion AI logic with multi-currency checkout & Indian GST compliance.
          </p>
        </div>

        {/* Currency Switcher with Auto-Detection Badge */}
        <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
          {detectedLabel && (
            <span className="text-[9px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-lg uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
              Auto-Detected: {detectedLabel}
            </span>
          )}
          <div className="flex items-center gap-1.5 bg-black/60 p-1.5 rounded-xl border border-white/10">
            <span className="text-[9px] font-black uppercase text-gray-400 px-2 tracking-widest">Currency:</span>
            {(Object.keys(CURRENCIES) as Currency[]).map((cur) => (
              <button
                key={cur}
                type="button"
                onClick={() => handleCurrencySelect(cur)}
                className={`px-3 py-1 text-xs font-mono font-black rounded-lg transition-all ${
                  currency === cur
                    ? 'bg-azure-500 text-black shadow-lg shadow-azure-500/20'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {CURRENCIES[cur].symbol} {cur}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* PLANS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        
        {/* Explorer */}
        <div className={`glass-panel p-8 rounded-3xl border border-white/10 flex flex-col justify-between relative transition-all ${
          subscriptionTier !== 'Explorer'
            ? 'bg-black/30 opacity-60 hover:opacity-90 hover:border-white/20'
            : 'bg-black/40 hover:border-azure/40'
        }`}>
          <div>
            <div className="flex justify-between items-center mb-4">
              <span className={`text-[10px] font-black uppercase tracking-widest ${subscriptionTier === 'Explorer' ? 'text-azure-400' : 'text-gray-500'}`}>Tier 01</span>
              {subscriptionTier === 'Explorer' && (
                <span className="text-[9px] font-black bg-azure/10 text-azure-400 px-2 py-0.5 rounded border border-azure/20 uppercase tracking-wider">Active</span>
              )}
            </div>
            <h3 className="text-2xl font-black text-white italic uppercase tracking-tight">Explorer</h3>
            <div className="mt-4 mb-6">
              <span className="text-4xl font-black text-white font-mono">{curConfig.symbol}0</span>
              <span className="text-xs text-gray-500 font-mono ml-1">/ Forever</span>
            </div>
            <ul className="space-y-3 text-xs text-gray-300">
              <li className="flex items-center gap-2">✓ Environmental Logic (Weather & Occasion)</li>
              <li className="flex items-center gap-2">✓ Personal Identity Scoring</li>
              <li className="flex items-center gap-2">✓ Standard Fashion Preview Visuals</li>
              <li className="flex items-center gap-2 text-gray-500">✗ Digital Closet Synchronization</li>
            </ul>
          </div>
          <button
            type="button"
            disabled={true}
            className="w-full mt-8 py-3 text-xs font-black uppercase tracking-widest bg-white/5 text-gray-500 border border-white/5 cursor-not-allowed opacity-40 transition-all"
            title={subscriptionTier === 'Explorer' ? 'Core logic baseline currently active' : 'Baseline discovery tier. Automatically reverts here if subscription expires.'}
          >
            {getButtonLabel('Explorer')}
          </button>
        </div>

        {/* Standard */}
        <div className={`glass-panel p-8 rounded-3xl border border-azure/40 flex flex-col justify-between relative transition-all ${
          isPremiumActive
            ? 'bg-black/30 opacity-60 hover:opacity-90 hover:border-azure/20'
            : 'bg-black/60 shadow-[0_0_50px_rgba(0,255,255,0.05)]'
        }`}>
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-azure-500 text-black text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-wider shadow-lg">
            Popular Choice
          </span>
          <div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-[10px] font-black text-azure-400 uppercase tracking-widest">Tier 02</span>
              {subscriptionTier === 'Standard' && (
                <span className="text-[9px] font-black bg-azure/10 text-azure-400 px-2 py-0.5 rounded border border-azure/20 uppercase tracking-wider">Active</span>
              )}
            </div>
            <h3 className="text-2xl font-black text-white italic uppercase tracking-tight">Standard</h3>
            <div className="mt-4 mb-6">
              <span className="text-4xl font-black text-white font-mono">{curConfig.symbol}{curConfig.prices.Standard}</span>
              <span className="text-xs text-gray-400 font-mono ml-1">/ Month</span>
            </div>
            <ul className="space-y-3 text-xs text-gray-300">
              <li className="flex items-center gap-2">✓ High-Fidelity 1024px Fashion Previews</li>
              <li className="flex items-center gap-2">✓ Culture & Tradition Analysis Engine</li>
              <li className="flex items-center gap-2">✓ 10 Intelligence Credits Included</li>
              <li className="flex items-center gap-2 text-gray-500">✗ Digital Closet Synchronization</li>
            </ul>
          </div>
          <button
            type="button"
            disabled={isStandardActive || isPremiumActive}
            onClick={() => openCheckout('Standard')}
            className={`w-full mt-8 py-3 text-xs font-black uppercase tracking-widest transition-all ${
              (isStandardActive || isPremiumActive)
                ? 'bg-white/5 text-gray-500 border border-white/5 cursor-not-allowed opacity-60'
                : 'btn-azure'
            }`}
            title={
              isPremiumActive
                ? 'You currently hold an active Premium tier with infinite styling and full digital closet sync.'
                : (isStandardActive
                    ? `Standard cycle active for ${standardDaysRemaining} more days. Use Top-Up for extra credits or Upgrade to Premium.`
                    : 'Renew Standard Plan')
            }
          >
            {getButtonLabel('Standard')}
          </button>
        </div>

        {/* Premium */}
        <div className="glass-panel p-8 rounded-3xl border-2 border-gold-500/40 flex flex-col justify-between relative bg-black/80 shadow-[0_0_60px_rgba(234,179,8,0.1)]">
          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-500 text-black text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-wider shadow-lg">
            Complete Digital Closet
          </span>
          <div>
            <div className="flex justify-between items-center mb-4">
              <span className="text-[10px] font-black text-gold-400 uppercase tracking-widest">Tier 03 (Haute Couture)</span>
              {subscriptionTier === 'Premium' && (
                <span className="text-[9px] font-black bg-gold/10 text-gold-400 px-2 py-0.5 rounded border border-gold/20 uppercase tracking-wider">Active</span>
              )}
            </div>
            <h3 className="text-2xl font-black text-white italic uppercase tracking-tight">Premium</h3>
            <div className="mt-4 mb-6">
              <span className="text-4xl font-black text-gold-400 font-mono">{curConfig.symbol}{curConfig.prices.Premium}</span>
              <span className="text-xs text-gray-400 font-mono ml-1">/ Month</span>
            </div>
            <ul className="space-y-3 text-xs text-gray-300">
              <li className="flex items-center gap-2">✓ <strong>Digital Closet Sync</strong> (Full Wardrobe Transfer)</li>
              <li className="flex items-center gap-2">✓ <strong>Unlimited Style Orchestrations</strong></li>
              <li className="flex items-center gap-2">✓ On-Model 1024px Biometric Likeness</li>
              <li className="flex items-center gap-2">✓ Multi-Outfit Curation (2 Looks per Event)</li>
            </ul>
          </div>
          <button
            type="button"
            disabled={subscriptionTier === 'Premium'}
            onClick={() => openCheckout('Premium')}
            className={`w-full mt-8 py-3 text-xs font-black uppercase tracking-widest transition-all rounded-lg ${
              subscriptionTier === 'Premium'
                ? 'bg-white/5 text-gray-500 border border-white/5 cursor-not-allowed opacity-60'
                : 'bg-gold hover:bg-amber-400 text-black font-black shadow-lg shadow-gold/30 hover:scale-[1.01] cursor-pointer'
            }`}
            title={subscriptionTier === 'Premium' ? 'Full Digital Closet Synchronization currently deployed' : 'Escalate to Premium (Full Closet Sync & Unlimited Generation)'}
          >
            {getButtonLabel('Premium')}
          </button>
        </div>

      </div>

      {/* TOP-UP CREDITS CARD */}
      <div className={`glass-panel p-6 rounded-2xl border ${canTopup ? 'border-azure-500/30 bg-azure-950/10' : 'border-white/10 bg-black/40'} flex flex-col md:flex-row justify-between items-center gap-6`}>
        <div>
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-black text-white uppercase tracking-wider">Need Additional Intelligence Credits?</h4>
            {canTopup ? (
              <span className="px-2 py-0.5 rounded text-[10px] font-black font-mono uppercase bg-azure-500/20 text-azure-400 border border-azure-500/30">
                Unlocked
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded text-[10px] font-black font-mono uppercase bg-white/5 text-gray-500 border border-white/10">
                Locked
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 font-mono mt-1">
            Top-up 10 AI Generation Credits for instant style previews without upgrading your tier.
          </p>
          {!canTopup && topupDisabledReason && (
            <p className="text-[11px] text-amber-400/90 font-mono mt-1.5 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{topupDisabledReason}</span>
            </p>
          )}
        </div>
        <button
          type="button"
          disabled={!canTopup}
          onClick={() => openCheckout('10_CREDITS')}
          className={`text-xs font-black uppercase tracking-widest py-2.5 px-6 whitespace-nowrap transition-all ${
            canTopup
              ? 'btn-azure shadow-lg shadow-azure-500/20 hover:scale-[1.02] cursor-pointer'
              : 'bg-white/5 text-gray-500 border border-white/5 cursor-not-allowed opacity-60'
          }`}
          title={!canTopup ? topupDisabledReason : 'Purchase 10 AI Generation Credits'}
        >
          Top-Up 10 Credits ({curConfig.symbol}{curConfig.prices.Topup10})
        </button>
      </div>

      {/* INVOICES & BILLING HISTORY SECTION */}
      <div className="space-y-4">
        <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
          <svg className="w-4 h-4 text-azure-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          Official Tax Invoices & Payment Receipts (GST 18%)
        </h3>

        {loadingInvoices ? (
          <p className="text-xs text-gray-500 font-mono py-4">Syncing invoice ledger...</p>
        ) : invoices.length === 0 ? (
          <div className="glass-panel p-6 rounded-xl border border-white/5 text-center text-xs text-gray-500 font-mono">
            No billing records registered yet. Invoices are generated automatically on payment.
          </div>
        ) : (
          <div className="glass-panel rounded-xl border border-white/10 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-black/60 text-gray-400 uppercase font-mono text-[10px] border-b border-white/10">
                <tr>
                  <th className="p-3">Invoice No</th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Plan / Item</th>
                  <th className="p-3">Amount Paid</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Refund Policy</th>
                  <th className="p-3 text-right">Tax Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 font-mono">
                {invoices.map((inv) => {
                  const invoiceDate = new Date(inv.createdAt);
                  const daysSince = (Date.now() - invoiceDate.getTime()) / (1000 * 60 * 60 * 24);
                  const isRefunded = inv.status === 'REFUNDED';
                  const isExhaustedOrExpired = daysSince > 7 || (inv.tier === 'Standard' && credits === 0);

                  return (
                    <tr key={inv.id} className="hover:bg-white/[0.02]">
                      <td className="p-3 font-bold text-white">{inv.invoiceNumber}</td>
                      <td className="p-3 text-gray-400">{new Date(inv.createdAt).toLocaleDateString()}</td>
                      <td className="p-3 text-gray-300">{inv.tier} ({inv.type})</td>
                      <td className="p-3 font-bold text-azure-400">{inv.currency} {inv.totalAmount?.toFixed(2)}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                          inv.status === 'PAID' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="p-3">
                        {isRefunded ? (
                          <span className="text-[9px] font-black text-red-400 uppercase tracking-wider">Reversed</span>
                        ) : isExhaustedOrExpired ? (
                          <span className="text-[9px] font-black text-gray-500 uppercase tracking-wider" title="Credits utilized or cooling-off period expired">
                            Non-Refundable
                          </span>
                        ) : (
                          <span className="text-[9px] font-black text-emerald-400/90 uppercase tracking-wider" title="Eligible for refund within 7-day cooling-off window if unutilized">
                            7d Window Active
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <a
                          href={`/api/getInvoices?invoiceId=${inv.id}&format=html`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[10px] font-black text-azure-400 hover:text-white underline uppercase tracking-wider inline-flex items-center gap-1"
                        >
                          Download PDF/HTML ↗
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CHECKOUT MODAL WITH LIVE GST CALCULATION */}
      {showCheckoutModal && (
        <div className="fixed inset-0 z-[300] bg-black/90 flex items-center justify-center p-4">
          <div className="bg-[#05070c] border-2 border-azure-500 p-6 md:p-8 rounded-3xl max-w-md w-full space-y-6 shadow-[0_0_80px_rgba(0,0,0,1)] text-white max-h-[90vh] overflow-y-auto">
            
            <div className="flex justify-between items-start border-b border-white/10 pb-4">
              <div>
                <span className="text-[9px] font-black uppercase text-azure-400 tracking-widest block">Secure Razorpay Checkout</span>
                <h3 className="text-xl font-black text-white italic uppercase tracking-tight mt-1">
                  Deploy {selectedPlan === '10_CREDITS' ? '10 Credits Top-Up' : `${selectedPlan} Tier`}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowCheckoutModal(false)}
                className="text-gray-400 hover:text-white text-sm uppercase font-bold"
              >
                ✕
              </button>
            </div>

            {/* Customer Billing Details */}
            <div className="space-y-3 p-3.5 bg-black/40 border border-white/5 rounded-2xl">
              <span className="text-[9px] font-black uppercase text-azure-400 tracking-widest block">
                Billing Recipient Details
              </span>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider block">
                  Full Name / Organization
                </label>
                <input
                  type="text"
                  id="checkout-billing-name"
                  name="name"
                  value={billingName}
                  autoComplete="name"
                  autoCapitalize="words"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => setBillingName(e.target.value)}
                  placeholder="e.g. Archana Sharma"
                  className="w-full bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-azure-400"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider block">
                  Billing Email <span className="text-azure-400">*</span>
                </label>
                <input
                  type="email"
                  id="checkout-billing-email"
                  name="email"
                  value={billingEmail}
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => {
                    setBillingEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  placeholder="name@domain.com"
                  className={`w-full bg-black/60 border rounded-xl px-3 py-2 text-xs font-mono text-white focus:outline-none ${
                    emailError ? 'border-red-500 focus:border-red-400' : 'border-white/10 focus:border-azure-400'
                  }`}
                />
                {emailError ? (
                  <p className="text-[10px] font-bold font-mono text-red-400">{emailError}</p>
                ) : (
                  <p className="text-[9px] text-gray-500 font-mono">
                    Official GST Tax Invoice and Razorpay payment receipt will be delivered here.
                  </p>
                )}
              </div>
            </div>

            {/* Promo Code Input */}
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider block">
                Promo / Access Code (Try <code>BETA50</code> or <code>BETA10</code>)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="checkout-promo-code"
                  name="couponCode"
                  value={couponCode}
                  autoComplete="off"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="e.g. BETA50"
                  className="bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono text-white flex-1 focus:outline-none focus:border-azure-400"
                />
                <button
                  type="button"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                  className="btn-azure text-[10px] font-black uppercase px-4 py-2 rounded-xl disabled:opacity-40"
                >
                  {couponLoading ? '...' : 'Apply'}
                </button>
              </div>
              {couponFeedback && (
                <p className={`text-[10px] font-bold font-mono ${couponFeedback.isValid ? 'text-emerald-400' : 'text-red-400'}`}>
                  {couponFeedback.message}
                </p>
              )}
            </div>

            {/* Tax Jurisdiction: State selection for INR domestic GST, or clean Export badge for Foreign Currency */}
            {currency === 'INR' ? (
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-wider block">
                  State / Tax Jurisdiction (Indian GST)
                </label>
                <select
                  value={isHaryana ? 'HR' : 'OTHER'}
                  onChange={(e) => {
                    const hr = e.target.value === 'HR';
                    setIsHaryana(hr);
                    setBillingState(hr ? 'Haryana' : 'Other State');
                  }}
                  className="w-full bg-black/60 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-azure-400"
                >
                  <option value="HR">Haryana (CGST 9% + SGST 9%)</option>
                  <option value="OTHER">Other Indian State (IGST 18%)</option>
                </select>
              </div>
            ) : (
              <div className="p-3 bg-azure-500/5 border border-azure-500/20 rounded-xl flex items-center justify-between text-xs font-mono">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Tax Jurisdiction</span>
                <span className="text-emerald-400 font-bold text-[11px] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  International Export (0% GST under LUT)
                </span>
              </div>
            )}

            {/* Live Price Breakdown */}
            <div className="bg-black/60 p-4 rounded-xl border border-white/10 space-y-2 text-xs font-mono">
              <div className="flex justify-between text-gray-400">
                <span>Base Price:</span>
                <span>{curConfig.symbol}{rawBasePrice.toFixed(2)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-emerald-400 font-bold">
                  <span>Discount ({discountPercent}%):</span>
                  <span>-{curConfig.symbol}{discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-400">
                <span>Taxable Value:</span>
                <span>{curConfig.symbol}{taxableValue.toFixed(2)}</span>
              </div>
              {isDomestic ? (
                isHaryana ? (
                  <>
                    <div className="flex justify-between text-gray-400">
                      <span>CGST (9%):</span>
                      <span>{curConfig.symbol}{cgstAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>SGST (9%):</span>
                      <span>{curConfig.symbol}{sgstAmount.toFixed(2)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-gray-400">
                    <span>IGST (18%):</span>
                    <span>{curConfig.symbol}{igstAmount.toFixed(2)}</span>
                  </div>
                )
              ) : (
                <div className="flex justify-between text-gray-500">
                  <span>Export LUT (0% GST):</span>
                  <span>{curConfig.symbol}0.00</span>
                </div>
              )}
              <div className="border-t border-azure/40 pt-2 flex justify-between text-sm font-black text-azure-400">
                <span>Total Payable:</span>
                <span>{curConfig.symbol}{finalPayable.toFixed(2)}</span>
              </div>
            </div>

            {/* Digital Waiver & Refund Disclosure */}
            <p className="text-[9px] text-gray-500 font-mono leading-relaxed text-center px-2">
              By proceeding, you agree to immediate digital delivery of generation credits and features. Under our <a href="/terms" target="_blank" rel="noreferrer" className="text-azure-400 hover:underline">Terms of Service</a>, AI styling credits and subscriptions are non-refundable once utilized.
            </p>

            {/* Action Button */}
            <button
              type="button"
              onClick={handleProceedPayment}
              className="btn-azure w-full py-3.5 text-xs font-black uppercase tracking-widest shadow-xl shadow-azure-500/20"
            >
              {finalPayable === 0 ? 'Activate Free Trial Access' : `Pay ${curConfig.symbol}${finalPayable.toFixed(2)} via Razorpay`}
            </button>

          </div>
        </div>
      )}

    </div>
  );
};

export default SubscriptionManager;